import { useCallback, useEffect, useMemo, useState } from 'react';
import { IntlPhoneField } from '../forms/IntlPhoneField';
import { Modal } from '../ui/Modal';
import { useCrmData } from '../../contexts/CrmDataContext';
import { usePortal } from '../../contexts/PortalContext';
import { useModalFormSession } from '../../hooks/useModalFormSession';
import {
  isTempAccountCode,
  isValidPermanentAccountCode,
  resolveAccountCodeForSave,
} from '../../lib/accountCode';
import {
  logAccountCodeAssigned,
  logAccountCreated,
  logAccountProfileUpdate,
  buildProfileChangeDetail,
  type ProfileFormValues,
} from '../../lib/accountActivityLog';
import { logPortalActivityQuiet } from '../../lib/portalActivityLog';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import { saveClientRecord } from '../../lib/saveClient';
import { normalizeStoredPhone, phoneInputValue } from '../../lib/intlPhone';
import { canViewAllAccounts } from '../../lib/accountVisibility';
import type { Client } from '../../types/crm';
import { CLIENT_TYPES, IT_CLIENT_TYPES } from '../../types/crm';
import { formatClientSalespersonCode, idsMatch, userCanWriteClient } from '../../utils/crmHelpers';
import {
  formatSalespersonCode,
  formatSalespersonCodes,
  normalizeSalespersonCode,
  normalizeSalespersonCodes,
} from '../../utils/salespersonScope';
import { resolveDistrictFromAddress } from '../../utils/singaporeDistrict';
import { AcraUenTypeahead } from './AcraUenTypeahead';
import {
  accountAcraStatus,
  acraRegistryFromEntity,
  isAcraActiveStatus,
  normalizeIntelProfileRegistry,
  sanitizeIntelProfileRegistry,
} from '../../lib/acraRegistry';
import { getAcraEntityByUen } from '../../lib/acraEntitiesDb';
import { isUenMarkedNa, normalizeClientUen, UEN_NA } from '../../lib/uenValue';
import type { AccountIntelRegistry } from '../../types/crm';

export interface AccountFormSaveResult {
  code: string;
  codeChanged: boolean;
}

interface AccountFormModalProps {
  open: boolean;
  onClose: () => void;
  client?: Client | null;
  /** When set, creates a sub-company / brand under this parent account. */
  parentClient?: Client | null;
  onSaved?: (result: AccountFormSaveResult) => void;
}

export function AccountFormModal({
  open,
  onClose,
  client,
  parentClient,
  onSaved,
}: AccountFormModalProps) {
  const { kind: portal } = usePortal();
  const isItPortal = portal === 'it';
  const accountTypes = isItPortal ? IT_CLIENT_TYPES : CLIENT_TYPES;
  const defaultAccountType = accountTypes[0];
  const { data, refresh, effectiveRole, ownSalespersonCodes, masterSalespersonCodes, actorSalespersonCodes, departmentSlug } =
    useCrmData();
  const isEdit = Boolean(client?.id);

  /** Parent for new brands, or resolved parent when editing a brand. */
  const linkedParent = useMemo(() => {
    if (parentClient) return parentClient;
    if (!client?.parent_client_id) return null;
    return data.clients.find((c) => idsMatch(c.id, client.parent_client_id)) ?? null;
  }, [parentClient, client?.parent_client_id, data.clients]);

  const isBrandAccount = Boolean(linkedParent);
  const inheritedClientType =
    linkedParent?.client_type?.trim() || defaultAccountType;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const elevatedAccountCreate = canViewAllAccounts(effectiveRole, departmentSlug);

  /** Elevated: User Management master list. Others: only their assigned codes. */
  const salespersonChoices = useMemo(() => {
    if (elevatedAccountCreate && masterSalespersonCodes.length > 0) {
      return masterSalespersonCodes;
    }
    return formatSalespersonCodes(ownSalespersonCodes);
  }, [elevatedAccountCreate, masterSalespersonCodes, ownSalespersonCodes]);

  const canEditTempCode =
    isEdit &&
    Boolean(client) &&
    isTempAccountCode(client?.code) &&
    userCanWriteClient(client ?? undefined, effectiveRole, actorSalespersonCodes, departmentSlug);

  const [companyName, setCompanyName] = useState(client?.company_name ?? '');
  const [code, setCode] = useState(client?.code ?? '');
  const [clientType, setClientType] = useState(client?.client_type ?? defaultAccountType);
  const [mainPhone, setMainPhone] = useState(() => phoneInputValue(client?.main_phone));
  const [website, setWebsite] = useState(client?.website ?? '');
  const [uen, setUen] = useState(client?.uen ?? '');
  const [acraRegistry, setAcraRegistry] = useState<AccountIntelRegistry | null>(
    () => normalizeIntelProfileRegistry(client?.intel_profile_json),
  );
  const [description, setDescription] = useState(client?.description ?? '');
  const [address, setAddress] = useState(client?.address ?? '');
  const [selectedSalespersonCode, setSelectedSalespersonCode] = useState('');

  const sessionKey = `${client?.id ?? 'new'}:${parentClient?.id ?? ''}`;

  const initForm = useCallback(() => {
    setCompanyName(client?.company_name ?? '');
    setCode(client?.code ?? '');
    setClientType(
      linkedParent?.client_type?.trim() ||
        client?.client_type ||
        defaultAccountType,
    );
    setMainPhone(phoneInputValue(client?.main_phone));
    setWebsite(client?.website ?? '');
    setUen(client?.uen ?? '');
    setAcraRegistry(normalizeIntelProfileRegistry(client?.intel_profile_json));
    setDescription(client?.description ?? '');
    setAddress(client?.address ?? '');
    const preferredSalesperson =
      linkedParent?.owner_id?.trim() ||
      ownSalespersonCodes[0] ||
      salespersonChoices[0] ||
      '';
    setSelectedSalespersonCode(preferredSalesperson);
    setError(null);
    setWarning(null);
  }, [
    client,
    defaultAccountType,
    linkedParent?.client_type,
    linkedParent?.owner_id,
    ownSalespersonCodes,
    salespersonChoices,
  ]);

  useModalFormSession(open, sessionKey, initForm);

  const autoDistrict = useMemo(() => resolveDistrictFromAddress(address), [address]);

  const acraStatus = accountAcraStatus({ uen, intel_profile_json: acraRegistry });
  const uenLegalName = acraRegistry?.legalName?.trim() || '';

  useEffect(() => {
    if (!open) return;
    const normalized = uen.trim().toUpperCase();
    if (!normalized || isUenMarkedNa(normalized)) return;
    if (
      acraRegistry?.uen?.trim().toUpperCase() === normalized &&
      acraRegistry.legalName?.trim()
    ) {
      return;
    }
    let cancelled = false;
    void getAcraEntityByUen(normalized).then((row) => {
      if (cancelled || !row) return;
      setAcraRegistry(acraRegistryFromEntity(row));
    });
    return () => {
      cancelled = true;
    };
  }, [open, uen, acraRegistry?.uen, acraRegistry?.legalName]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const name = companyName.trim();
    if (!name) {
      setError('Company name is required.');
      return;
    }

    const rawCode = code.trim();
    let accountCode: string;
    let codeChanged = false;

    if (isEdit && client) {
      if (canEditTempCode && rawCode !== client.code) {
        if (!isValidPermanentAccountCode(rawCode)) {
          setError('Enter the permanent customer code from Finance / NAV (not TEMP).');
          return;
        }
        const duplicateCode = data.clients.find(
          (c) => c.code.toLowerCase() === rawCode.toLowerCase() && c.id !== client.id,
        );
        if (duplicateCode) {
          setError(`Account code "${rawCode}" is already in use.`);
          return;
        }
        accountCode = rawCode;
        codeChanged = true;
      } else {
        accountCode = client.code;
      }
    } else {
      accountCode = resolveAccountCodeForSave(rawCode, true);
      if (rawCode) {
        const duplicateCode = data.clients.find(
          (c) => c.code.toLowerCase() === accountCode.toLowerCase(),
        );
        if (duplicateCode) {
          setError(`Account code "${accountCode}" is already in use.`);
          return;
        }
      }
    }

    const duplicate = data.clients.find(
      (c) => c.company_name.toLowerCase() === name.toLowerCase() && c.id !== client?.id,
    );
    if (duplicate) {
      setError(`Account name "${name}" already exists.`);
      return;
    }

    const profilePayload: ProfileFormValues = {
      company_name: name,
      client_type: isBrandAccount ? inheritedClientType : clientType,
      main_phone: normalizeStoredPhone(mainPhone),
      website: website.trim(),
      uen: normalizeClientUen(uen),
      description: description.trim(),
      address: address.trim(),
      district: resolveDistrictFromAddress(address),
    };

    if (!isEdit && !isItPortal) {
      const newSalespersonCode = formatSalespersonCode(
        linkedParent?.owner_id?.trim() || selectedSalespersonCode || '',
      );
      if (!newSalespersonCode) {
        setError(
          elevatedAccountCreate
            ? 'Please select a salesperson code for this account.'
            : 'No salesperson code is assigned to your profile. Please contact your administrator to request one before creating accounts.',
        );
        return;
      }
      if (!linkedParent && !elevatedAccountCreate) {
        const ownNormalized = normalizeSalespersonCodes(ownSalespersonCodes);
        if (!ownNormalized.includes(normalizeSalespersonCode(newSalespersonCode))) {
          setError('Please select one of your assigned salesperson codes.');
          return;
        }
      }
    }

    const newSalespersonCode = isItPortal
      ? ''
      : formatSalespersonCode(
          linkedParent?.owner_id?.trim() || selectedSalespersonCode || '',
        );

    setSaving(true);
    setError(null);
    setWarning(null);

    const intelProfile = sanitizeIntelProfileRegistry(acraRegistry, profilePayload.uen);
    const savePayload = {
      ...profilePayload,
      code: accountCode,
      intel_profile_json:
        profilePayload.uen && !isUenMarkedNa(profilePayload.uen) ? intelProfile : null,
    };

    try {
      const { error: saveError, clientId: savedId } = await saveClientRecord(
        savePayload,
        isEdit && client
          ? {
              clientId: client.id,
              // Parent accounts push type to all brands under them.
              syncBrandTypes: !client.parent_client_id,
            }
          : {
              newSalespersonCode,
              parentClientId: linkedParent?.id ?? parentClient?.id ?? null,
              allowEmptyOwner: isItPortal,
            },
      );
      if (saveError) throw saveError;

      let activityWarning: string | null = null;
      try {
        if (isEdit && client) {
          await logAccountProfileUpdate(client.id, client, profilePayload);
          if (codeChanged) {
            await logAccountCodeAssigned(client.id, client.code, accountCode);
          }
          const detail = buildProfileChangeDetail(client, profilePayload);
          logPortalActivityQuiet({
            portal,
            action: 'update',
            entityType: 'account',
            entityId: client.id,
            clientId: client.id,
            title: `Account updated: ${name}`,
            detail:
              detail ??
              (codeChanged
                ? `Customer code ${client.code} → ${accountCode}.`
                : `Account "${name}" profile updated.`),
          });
        } else if (savedId) {
          await logAccountCreated(savedId, name);
          logPortalActivityQuiet({
            portal,
            action: 'create',
            entityType: 'account',
            entityId: savedId,
            clientId: savedId,
            title: linkedParent ? `Sub-company created: ${name}` : `Account created: ${name}`,
            detail: linkedParent
              ? isItPortal
                ? `Code ${accountCode}. Under ${linkedParent.company_name} (${linkedParent.code}).`
                : `Code ${accountCode}. Under ${linkedParent.company_name} (${linkedParent.code}). SP ${newSalespersonCode}.`
              : isItPortal
                ? `Code ${accountCode}.`
                : `Code ${accountCode}. SP ${newSalespersonCode}.`,
          });
        }
      } catch (logErr) {
        activityWarning = formatSupabaseError(logErr);
      }

      await refresh().catch(() => undefined);
      if (activityWarning) {
        setWarning(
          isEdit
            ? `Account details were saved. The activity timeline could not be updated. ${activityWarning}`
            : `Account was created. The activity timeline could not be updated. ${activityWarning}`,
        );
        return;
      }
      onSaved?.({ code: accountCode, codeChanged });
      onClose();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  const showSalespersonPicker =
    !isItPortal &&
    !isEdit &&
    !linkedParent &&
    (elevatedAccountCreate || salespersonChoices.length > 1);

  const salespersonDisplay = isEdit
    ? formatClientSalespersonCode(client?.owner_id)
    : linkedParent?.owner_id?.trim()
      ? formatClientSalespersonCode(linkedParent.owner_id)
      : selectedSalespersonCode || '—';

  const modalTitle = isEdit
    ? 'Edit account'
    : linkedParent
      ? `New sub-company — ${linkedParent.company_name}`
      : 'New account';

  const codeFieldDisabled = isEdit && !canEditTempCode;
  const cannotCreate =
    !isItPortal &&
    !isEdit &&
    !linkedParent &&
    (elevatedAccountCreate
      ? salespersonChoices.length === 0
      : normalizeSalespersonCodes(ownSalespersonCodes).length === 0);

  return (
    <Modal title={modalTitle} open={open} onClose={onClose} wide panelClassName="modal-panel--task-form">
      <form className="crm-form crm-form--modal" onSubmit={(e) => void handleSave(e)}>
        <div className="crm-form__scroll">
        {error && <div className="alert alert-error">{error}</div>}
        {warning && <div className="alert alert-warn">{warning}</div>}

        {cannotCreate && (
          <div className="alert alert-warn">
            No salesperson code is assigned to your profile. Please contact your administrator to request
            one before creating accounts.
          </div>
        )}

        {linkedParent && !isEdit && (
          <div className="inline-notice inline-notice--readonly">
            <span className="inline-notice__icon" aria-hidden>
              ℹ
            </span>
            <span>
              This brand will be registered under <strong>{linkedParent.company_name}</strong> (
              {linkedParent.code}). It will maintain its own contacts, projects, and tasks.
            </span>
          </div>
        )}

        <div className="crm-form__grid">
          <label className="field">
            <span className="field-label">Company name *</span>
            <input
              className="field-input"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />
            {uenLegalName ? (
              <p className="field-hint account-form__uen-legal-name" title="ACRA registered name">
                {uenLegalName}
              </p>
            ) : null}
          </label>
          <label className="field">
            <span className="field-label">
              {canEditTempCode ? 'Customer code *' : isEdit ? 'Account code' : 'Account code (optional)'}
            </span>
            <input
              className="field-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required={false}
              disabled={codeFieldDisabled}
              placeholder={!isEdit ? 'Leave blank to generate a temporary code' : undefined}
            />
            {!isEdit && (
              <p className="field-hint">Leave blank for a temporary code. Finance will assign a permanent code later.</p>
            )}
            {canEditTempCode && (
              <p className="field-hint">Enter the permanent code. It cannot be changed after saving.</p>
            )}
            {isEdit && !canEditTempCode && (
              <p className="field-hint">This code cannot be changed after assignment.</p>
            )}
          </label>
          <label className="field">
            <span className="field-label">{isItPortal ? 'Status' : 'Account type'}</span>
            <select
              className="field-input"
              value={isBrandAccount ? inheritedClientType : clientType}
              onChange={(e) => setClientType(e.target.value)}
              disabled={isBrandAccount}
            >
              {accountTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
              {isBrandAccount &&
              inheritedClientType &&
              !(accountTypes as readonly string[]).includes(inheritedClientType) ? (
                <option value={inheritedClientType}>{inheritedClientType}</option>
              ) : null}
              {!isBrandAccount &&
              clientType &&
              !(accountTypes as readonly string[]).includes(clientType) ? (
                <option value={clientType}>{clientType}</option>
              ) : null}
            </select>
            {isBrandAccount ? (
              <p className="field-hint">
                Follows the parent account {isItPortal ? 'status' : 'type'}. Change it on{' '}
                {linkedParent?.company_name ?? 'the parent'} to update this brand.
              </p>
            ) : (
              <p className="field-hint">
                Brands under this account inherit this {isItPortal ? 'status' : 'type'} automatically.
              </p>
            )}
          </label>
          {!isItPortal ? (
          <label className="field">
            <span className="field-label">Salesperson code</span>
            {showSalespersonPicker ? (
              salespersonChoices.length > 0 ? (
                <select
                  className="field-input mono"
                  value={selectedSalespersonCode}
                  onChange={(e) => setSelectedSalespersonCode(e.target.value)}
                  required
                >
                  {!selectedSalespersonCode ? (
                    <option value="" disabled>
                      Select salesperson code
                    </option>
                  ) : null}
                  {salespersonChoices.map((spCode) => (
                    <option key={spCode} value={spCode}>
                      {spCode}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="field-input mono"
                  value={selectedSalespersonCode}
                  onChange={(e) => setSelectedSalespersonCode(e.target.value)}
                  placeholder="Enter salesperson code"
                  required
                />
              )
            ) : (
              <input className="field-input mono" value={salespersonDisplay} disabled readOnly />
            )}
            <p className="field-hint">
              {isEdit
                ? 'Use Transfer account to change the salesperson code.'
                : linkedParent
                  ? 'Inherits the parent account salesperson code.'
                  : elevatedAccountCreate
                    ? 'From User Management salesperson codes.'
                    : salespersonChoices.length > 1
                      ? 'Select your salesperson code.'
                      : 'Uses your assigned salesperson code.'}
            </p>
          </label>
          ) : null}
          <label className="field">
            <span className="field-label">Main phone</span>
            <IntlPhoneField value={mainPhone} onChange={setMainPhone} />
          </label>
          <label className="field">
            <span className="field-label">Website</span>
            <input className="field-input" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">UEN</span>
            <div className="account-form__uen-row">
              <AcraUenTypeahead
                value={uen}
                onChange={setUen}
                onManualInput={() => setAcraRegistry(null)}
                onRegistryPick={(registry) => {
                  setAcraRegistry(registry);
                  if (!companyName.trim() && registry.legalName) {
                    setCompanyName(registry.legalName);
                  }
                }}
              />
              <button
                type="button"
                className="btn-secondary btn-sm account-form__uen-na-btn"
                onClick={() => {
                  setUen(UEN_NA);
                  setAcraRegistry(null);
                }}
              >
                Set N/A
              </button>
            </div>
            {isUenMarkedNa(uen) ? (
              <p className="field-hint">N/A = no Singapore UEN / not applicable.</p>
            ) : (
              <p className="field-hint">
                Type UEN or company name (3+ characters), or Set N/A if there is no UEN.
              </p>
            )}
          </label>
          <label className="field">
            <span className="field-label">ACRA status</span>
            <div
              className="field-input field-input--readonly account-form__acra-status-field"
              aria-readonly="true"
            >
              {isUenMarkedNa(uen) ? (
                <span className="account-form__acra-status-empty">N/A</span>
              ) : acraStatus ? (
                <span
                  className={`account-form__acra-status-text${
                    isAcraActiveStatus(acraStatus)
                      ? ' account-form__acra-status-text--active'
                      : ' account-form__acra-status-text--inactive'
                  }`}
                >
                  {acraStatus}
                </span>
              ) : (
                <span className="account-form__acra-status-empty">—</span>
              )}
            </div>
          </label>
        </div>

        <label className="field">
          <span className="field-label">Description</span>
          <textarea
            className="field-input field-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </label>
        <label className="field">
          <span className="field-label">Office address</span>
          <textarea
            className="field-input field-textarea"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={3}
            placeholder="Enter street, unit and postal code"
          />
          <p className="field-hint">
            {autoDistrict
              ? `District (automatic): ${autoDistrict}`
              : 'District is assigned automatically from the office address or six-digit postal code.'}
          </p>
        </label>

        </div>

        <div className="crm-form__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving || cannotCreate}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create account'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
